// First step of the Project Viewer's "Link … to Tools" options: the BQE record has
// no Tools record pointing at it, so either create a new Tools record for it, or
// pick an existing Tools record (e.g. one created before BQE was set up, or linked
// to the wrong BQE record) and re-point it at this BQE record.
import React, { useState } from "react";

import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";

// Same "active mode" look as the console dialogs' Search Existing / Create New buttons.
const activeModeButtonSx = (isDisabledBecauseActive) =>
	isDisabledBecauseActive
		? { "&.Mui-disabled": { background: "#184C93", color: "#ffffff" } }
		: undefined;

// `entityLabel` is "Property" / "Project" / "Phase". `options` are the candidate Tools
// records; `getOptionLabel` names one; `noOptionsText` explains an empty list. `onCreateNew()` / `onUseExisting(record)` hand
// off to the matching Add / Edit dialog. Mounted only while open, so its state starts
// fresh each time.
const LinkToolsRecordDialog = ({
	open,
	entityLabel,
	bqeName,
	options,
	getOptionLabel,
	noOptionsText,
	onCreateNew,
	onUseExisting,
	onClose,
}) => {
	const [mode, setMode] = useState("existing");
	const [selected, setSelected] = useState(null);

	const lowerLabel = entityLabel.toLowerCase();

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle id="LinkToolsRecordDialogTitle">Link {entityLabel} to Tools</DialogTitle>
			<DialogContent id="LinkToolsRecordDialogContent">
				<div>
					<strong>{bqeName}</strong> isn't linked to a Tools {lowerLabel} record yet.
				</div>

				<div className="linkToolsRecordSubtitleWithActions">
					<span>Tools {entityLabel} Record</span>
					<ButtonGroup size="small">
						<Button
							sx={activeModeButtonSx(mode === "existing")}
							variant={mode === "existing" ? "contained" : "outlined"}
							onClick={() => setMode("existing")}
							disabled={mode === "existing"}
						>
							Search Existing
						</Button>
						<Button
							sx={activeModeButtonSx(mode === "create")}
							variant={mode === "create" ? "contained" : "outlined"}
							onClick={() => setMode("create")}
							disabled={mode === "create"}
						>
							Create New
						</Button>
					</ButtonGroup>
				</div>

				{mode === "existing" ? (
					<Autocomplete
						options={options}
						getOptionLabel={getOptionLabel}
						value={selected}
						onChange={(event, value) => setSelected(value)}
						isOptionEqualToValue={(option, value) => option.id === value.id}
						noOptionsText={noOptionsText}
						renderInput={(params) => (
							<TextField {...params} label={`Tools ${entityLabel}`} variant="outlined" />
						)}
					/>
				) : (
					<div className="linkToolsRecordHint">
						The next step opens the Add {entityLabel} form with this BQE {lowerLabel} already selected.
					</div>
				)}

				{mode === "existing" && selected?.bqe_id && (
					<Alert severity="warning">
						This Tools {lowerLabel} is currently linked to BQE record{" "}
						{selected.bqe_code || selected.bqe_id}. Saving will re-point it to this BQE {lowerLabel}{" "}
						instead.
					</Alert>
				)}

				<div className="linkToolsRecordButtons">
					<Button variant="outlined" color="error" onClick={onClose}>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="primary"
						disabled={mode === "existing" && !selected}
						onClick={() => (mode === "create" ? onCreateNew() : onUseExisting(selected))}
					>
						Continue
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default LinkToolsRecordDialog;
